import React from "react";
import { Box, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import { CustomerKYCFullDataResponse } from "types/admin";
import NameValuePair from "components/pages/KYCsteps/confirmDetails/nameValuePair";
import { KycStatusChip } from "./KycVerificationSections";
import { formatDate } from "./adminKycVerificationUtils";

interface AdminCustomerInfoPanelProps {
  detail: CustomerKYCFullDataResponse;
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <NameValuePair name={label} value={value} emphasizeValue />
    </Grid>
  );
}

function formatAddressLines(detail: CustomerKYCFullDataResponse) {
  const streetLine = detail.address?.trim();
  const localityLine = [detail.city, detail.state, detail.postalCode].filter(Boolean).join(", ");

  return [streetLine, localityLine].filter(Boolean);
}

function AdminCustomerInfoPanel({ detail }: AdminCustomerInfoPanelProps) {
  const addressLines = formatAddressLines(detail);

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
        <Typography variant="h6" color="secondary.main">
          Customer Information
        </Typography>
        <KycStatusChip status={detail.kycStatus} />
      </Stack>

      <Grid container spacing={2.5}>
        <InfoField label="Customer ID" value={detail.customerId} />
        <InfoField label="Owner Name" value={detail.ownerName} />
        <InfoField label="Shop Name" value={detail.shopName} />
        <InfoField label="Mobile" value={detail.mobileNumber} />
        <InfoField label="Email" value={detail.emailId} />
        <InfoField label="Establishment Type" value={detail.establishmentType} />
        <InfoField label="KYC Last Updated" value={formatDate(detail.lastUpdatedAt)} />
        <InfoField label="Corn SAP ID" value={detail.cornSapId} />
        <InfoField label="CP SAP ID" value={detail.cpSapId} />

        {detail.remark && (
          <Grid item xs={12}>
            <NameValuePair name="Remark" value={detail.remark} emphasizeValue />
          </Grid>
        )}

        {addressLines.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, fontWeight: 500, letterSpacing: 0.2 }}
              >
                Address
              </Typography>
              <Stack spacing={0.5}>
                {addressLines.map((line) => (
                  <Typography
                    key={line}
                    variant="body2"
                    fontWeight={600}
                    sx={{ wordBreak: "break-word", lineHeight: 1.6 }}
                  >
                    {line}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

export default AdminCustomerInfoPanel;
